import React, { useState, useCallback } from 'react';
import { Spin, Empty, Pagination, Input, Select, Space, Button } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import MobileDataCard, { MobileDataCardProps, MobileCardField } from './MobileDataCard';
import SwipeableCard, { SwipeAction } from './SwipeableCard';

const { Search } = Input;

export interface MobileFilterOption {
  label: string;
  value: string;
}

export interface MobileFilterConfig {
  key: string;
  placeholder: string;
  options: MobileFilterOption[];
  value?: string;
  onChange?: (value: string) => void;
}

export interface MobilePaginationConfig {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
  showSizeChanger?: boolean;
  pageSizeOptions?: string[];
}

export interface SwipeActionConfig<T> {
  left?: {
    icon: React.ReactNode;
    color: string;
    label?: string;
    onClick: (record: T) => void;
  };
  right?: {
    icon: React.ReactNode;
    color: string;
    label?: string;
    onClick: (record: T) => void;
  };
}

export interface MobileDataListProps<T extends Record<string, any>> {
  data: T[];
  keyField: keyof T;
  cardConfig: Omit<MobileDataCardProps<T>, 'data'>;
  loading?: boolean;
  emptyText?: string;
  emptyDescription?: string;
  onRefresh?: () => void;
  pagination?: MobilePaginationConfig;
  swipeActions?: SwipeActionConfig<T>;
  searchPlaceholder?: string;
  onSearch?: (value: string) => void;
  filters?: MobileFilterConfig[];
  showFilters?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

function MobileDataList<T extends Record<string, any>>({
  data,
  keyField,
  cardConfig,
  loading = false,
  emptyText = 'No data',
  emptyDescription,
  onRefresh,
  pagination,
  swipeActions,
  searchPlaceholder = 'Search...',
  onSearch,
  filters = [],
  showFilters = true,
  header,
  footer,
}: MobileDataListProps<T>) {
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
    onSearch?.(value);
  }, [onSearch]);

  const renderCard = (record: T) => {
    const card = (
      <MobileDataCard
        key={String(record[keyField])}
        data={record}
        {...cardConfig}
      />
    );

    if (swipeActions?.left || swipeActions?.right) {
      return (
        <SwipeableCard
          key={String(record[keyField])}
          leftAction={swipeActions.left ? {
            icon: swipeActions.left.icon,
            color: swipeActions.left.color,
            label: swipeActions.left.label,
            onClick: () => swipeActions.left!.onClick(record),
          } : undefined}
          rightAction={swipeActions.right ? {
            icon: swipeActions.right.icon,
            color: swipeActions.right.color,
            label: swipeActions.right.label,
            onClick: () => swipeActions.right!.onClick(record),
          } : undefined}
        >
          {card}
        </SwipeableCard>
      );
    }

    return card;
  };

  return (
    <div className="mobile-data-list">
      {/* Header area with search and filters */}
      {(onSearch || filters.length > 0 || header) && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          {header}

          {/* Search bar */}
          {onSearch && (
            <div style={{ marginBottom: filters.length > 0 ? 12 : 0 }}>
              <Search
                placeholder={searchPlaceholder}
                allowClear
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onSearch={handleSearch}
                style={{ width: '100%' }}
                size="middle"
              />
            </div>
          )}

          {/* Filter toggle button */}
          {showFilters && filters.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                type={showFilterPanel ? 'primary' : 'default'}
                size="middle"
              >
                Filters {filters.filter(f => f.value).length > 0 && `(${filters.filter(f => f.value).length})`}
              </Button>

              {onRefresh && (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={onRefresh}
                  loading={loading}
                  size="middle"
                >
                  Refresh
                </Button>
              )}
            </div>
          )}

          {/* Filter panel (collapsible) */}
          {showFilterPanel && filters.length > 0 && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                backgroundColor: 'white',
                borderRadius: 8,
                border: '1px solid #f0f0f0',
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                {filters.map((filter) => (
                  <div key={filter.key}>
                    <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>
                      {filter.placeholder}
                    </div>
                    <Select
                      placeholder={filter.placeholder}
                      value={filter.value}
                      onChange={filter.onChange}
                      style={{ width: '100%' }}
                      allowClear
                      options={filter.options}
                    />
                  </div>
                ))}
              </Space>
            </div>
          )}
        </div>
      )}

      {/* Content area */}
      <div
        style={{
          padding: '12px 16px',
          minHeight: 200,
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : data.length === 0 ? (
          <Empty
            description={emptyDescription || emptyText}
            style={{ padding: 40 }}
          />
        ) : (
          <div>
            {data.map(renderCard)}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && data.length > 0 && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#fafafa',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={pagination.onChange}
            showSizeChanger={pagination.showSizeChanger ?? false}
            simple
            size="small"
          />
        </div>
      )}

      {/* Footer */}
      {footer && (
        <div style={{ padding: '12px 16px' }}>
          {footer}
        </div>
      )}
    </div>
  );
}

export default MobileDataList;
